(() => {
  const pendingClass = 'cart-popup--pending';
  const updatingClass = 'cart-popup--quantity-updating';
  const updatingRowClass = 'cart-item--quantity-updating';
  const addToCartButtonSelector = [
    '.mw-apo-add-to-cart-cloned',
    '.gempages-add-to-cart-button',
    'form[action*="/cart/add"] [name="add"]',
  ].join(',');
  const deliveryProductHandle = 'coppersmith-creations-uk-delivery';
  const apoMutationInterval = 200;
  const apoMutationStartDelay = 500;
  const maximumApoSettleTime = 6000;
  let pendingTimeout;

  const delay = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const getCartRoute = (routeName, fallback) => {
    if (typeof routes !== 'undefined' && routes[routeName]) return routes[routeName];

    const root = window.Shopify?.routes?.root || '/';
    return `${root}${fallback}`;
  };

  const getFetchConfig = () => {
    if (typeof fetchConfig === 'function') return fetchConfig();

    return {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };
  };

  const parseHtml = (html) => new DOMParser().parseFromString(html, 'text/html');

  const getItemRows = (root) => Array.from(root?.querySelectorAll('tr.cart-item[data-cart-item-key]') || []);

  const copyAttribute = (target, source, attributeName) => {
    if (!target || !source) return;

    if (source.hasAttribute(attributeName)) {
      target.setAttribute(attributeName, source.getAttribute(attributeName));
    } else {
      target.removeAttribute(attributeName);
    }
  };

  const copyAttributes = (target, source, attributeNames) => {
    attributeNames.forEach((attributeName) => copyAttribute(target, source, attributeName));
  };

  const rowFallbackKey = (row) => `${row.dataset.cartProductId || ''}:${row.dataset.cartVariantId || ''}`;

  const buildRowMatcher = (sourceRows) => {
    const unusedRows = new Set(sourceRows);

    return (targetRow) => {
      const exactMatch = sourceRows.find(
        (sourceRow) =>
          unusedRows.has(sourceRow) &&
          sourceRow.dataset.cartItemKey &&
          sourceRow.dataset.cartItemKey === targetRow.dataset.cartItemKey,
      );

      if (exactMatch) {
        unusedRows.delete(exactMatch);
        return exactMatch;
      }

      const fallbackKey = rowFallbackKey(targetRow);
      const fallbackMatch = sourceRows.find(
        (sourceRow) => unusedRows.has(sourceRow) && rowFallbackKey(sourceRow) === fallbackKey,
      );

      if (fallbackMatch) {
        unusedRows.delete(fallbackMatch);
        return fallbackMatch;
      }

      return null;
    };
  };

  const syncQuantityControl = (targetRow, sourceRow) => {
    const targetInput = targetRow.querySelector('.quantity__input');
    const sourceInput = sourceRow.querySelector('.quantity__input');

    if (targetInput && sourceInput) {
      copyAttributes(targetInput, sourceInput, [
        'data-quantity-variant-id',
        'data-cart-quantity',
        'data-min',
        'min',
        'max',
        'step',
        'aria-label',
        'id',
        'data-index',
      ]);
      targetInput.value = sourceInput.value;
      targetInput.defaultValue = sourceInput.value;
      targetInput.setAttribute('value', sourceInput.value);
    }

    const targetRemoveButton = targetRow.querySelector('cart-remove-button');
    const sourceRemoveButton = sourceRow.querySelector('cart-remove-button');
    copyAttributes(targetRemoveButton, sourceRemoveButton, ['id', 'data-index']);

    const targetRemoveAction = targetRemoveButton?.querySelector('button');
    const sourceRemoveAction = sourceRemoveButton?.querySelector('button');
    copyAttributes(targetRemoveAction, sourceRemoveAction, ['aria-label', 'data-variant-id']);

    const targetError = targetRow.querySelector('.cart-item__error');
    const sourceError = sourceRow.querySelector('.cart-item__error');
    copyAttribute(targetError, sourceError, 'id');
    const targetErrorText = targetError?.querySelector('.cart-item__error-text');
    if (targetErrorText) targetErrorText.textContent = '';
  };

  const normalizeDeliveryProperty = (root) => {
    root?.querySelectorAll('.product-option').forEach((option) => {
      const term = option.querySelector('dt')?.textContent.replace(/:\s*$/, '').trim();
      const value = option.querySelector('dd');

      if (term !== 'Select Your Delivery Location' || !value) return;

      const normalizedValue = value.textContent.replace(/\s*\(x\d+\)\s*$/, '').trim();
      if (value.textContent.trim() !== normalizedValue) value.textContent = normalizedValue;
    });
  };

  const syncRowNumbers = (targetRow, sourceRow) => {
    const targetPrice = targetRow.querySelector('.cart-item__price-wrapper');
    const sourcePrice = sourceRow.querySelector('.cart-item__price-wrapper');

    if (targetPrice && sourcePrice && targetPrice.innerHTML !== sourcePrice.innerHTML) {
      targetPrice.innerHTML = sourcePrice.innerHTML;
    }

    targetRow.id = sourceRow.id;
    targetRow.dataset.cartItemKey = sourceRow.dataset.cartItemKey;
    targetRow.dataset.cartVariantId = sourceRow.dataset.cartVariantId;
    targetRow.dataset.cartProductId = sourceRow.dataset.cartProductId;
    targetRow.dataset.cartProductHandle = sourceRow.dataset.cartProductHandle;
    targetRow.dataset.cartLineIndex = sourceRow.dataset.cartLineIndex;
    targetRow.classList.toggle('cart-item--no-image', sourceRow.classList.contains('cart-item--no-image'));
    targetRow.toggleAttribute(
      'data-cart-popup-delivery-line',
      sourceRow.hasAttribute('data-cart-popup-delivery-line'),
    );
    syncQuantityControl(targetRow, sourceRow);
    normalizeDeliveryProperty(targetRow);
  };

  const syncFooter = (sourceDocument) => {
    const drawer = document.querySelector('cart-drawer');
    const targetSubtotal = drawer?.querySelector('.drawer__inner > .drawer__footer .totals__total-value');
    const sourceSubtotal = sourceDocument.querySelector(
      'cart-drawer .drawer__inner > .drawer__footer .totals__total-value',
    );

    if (targetSubtotal && sourceSubtotal && targetSubtotal.innerHTML !== sourceSubtotal.innerHTML) {
      targetSubtotal.innerHTML = sourceSubtotal.innerHTML;
    }

    const targetSavings = drawer?.querySelector('.drawer__inner > .drawer__footer .cart-popup__savings');
    const sourceSavings = sourceDocument.querySelector(
      'cart-drawer .drawer__inner > .drawer__footer .cart-popup__savings',
    );

    if (targetSavings && sourceSavings) {
      if (targetSavings.innerHTML !== sourceSavings.innerHTML) {
        targetSavings.innerHTML = sourceSavings.innerHTML;
      }
      copyAttribute(targetSavings, sourceSavings, 'hidden');
    }

    const targetCheckout = drawer?.querySelector('#CartDrawer-Checkout');
    const sourceCheckout = sourceDocument.querySelector('#CartDrawer-Checkout');
    copyAttribute(targetCheckout, sourceCheckout, 'disabled');
  };

  const renderWholeDrawerInner = (sourceDocument) => {
    const drawer = document.querySelector('cart-drawer');
    const targetInner = drawer?.querySelector('.drawer__inner');
    const sourceDrawer = sourceDocument.querySelector('cart-drawer');
    const sourceInner = sourceDrawer?.querySelector('.drawer__inner');

    if (!drawer || !targetInner || !sourceInner) return;

    targetInner.className = sourceInner.className;
    targetInner.innerHTML = sourceInner.innerHTML;
    drawer.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
  };

  const reconcileDrawer = (sectionHtml, { allowStructuralChanges = false } = {}) => {
    if (!sectionHtml) return;

    const sourceDocument = parseHtml(sectionHtml);
    const sourceRows = getItemRows(sourceDocument);
    const drawer = document.querySelector('cart-drawer');
    const drawerItems = drawer?.querySelector('cart-drawer-items');
    const targetRows = getItemRows(drawerItems);

    if (allowStructuralChanges && (sourceRows.length === 0 || targetRows.length === 0)) {
      renderWholeDrawerInner(sourceDocument);
      return;
    }

    if (!drawerItems) return;

    const matchSourceRow = buildRowMatcher(sourceRows);
    const matchedSourceRows = new Set();

    targetRows.forEach((targetRow) => {
      const sourceRow = matchSourceRow(targetRow);

      if (!sourceRow) {
        if (allowStructuralChanges) targetRow.remove();
        return;
      }

      matchedSourceRows.add(sourceRow);
      syncRowNumbers(targetRow, sourceRow);
    });

    if (allowStructuralChanges) {
      const targetBody = drawerItems.querySelector('tbody');

      sourceRows.forEach((sourceRow) => {
        if (matchedSourceRows.has(sourceRow) || !targetBody) return;

        const newRow = sourceRow.cloneNode(true);
        newRow.classList.add('cart-item--cart-popup-new');
        targetBody.append(newRow);
        window.setTimeout(() => newRow.classList.remove('cart-item--cart-popup-new'), 180);
      });
    }

    const sourceDrawerItems = sourceDocument.querySelector('cart-drawer-items');
    drawerItems.classList.toggle('is-empty', sourceDrawerItems?.classList.contains('is-empty') || false);
    drawer.classList.toggle('is-empty', sourceDocument.querySelector('cart-drawer')?.classList.contains('is-empty') || false);
    syncFooter(sourceDocument);
    normalizeDeliveryProperty(drawerItems);
  };

  const syncCartBubble = (sectionHtml) => {
    if (!sectionHtml) return;

    const sourceDocument = parseHtml(sectionHtml);
    const targetBubble = document.getElementById('cart-icon-bubble');
    const sourceBubble = sourceDocument.getElementById('cart-icon-bubble') || sourceDocument.body.firstElementChild;

    if (targetBubble && sourceBubble) targetBubble.innerHTML = sourceBubble.innerHTML;
  };

  const getOrphanDeliveryUpdates = (cart) => {
    const items = cart?.items || [];
    const updates = {};

    items.forEach((item) => {
      if (item.handle !== deliveryProductHandle || !item.key) return;

      const properties = getApoProperties(item);
      const cartId = String(properties._mws_cart || '');
      const hasLinkedParent = cartId
        ? items.some((candidate) => {
            const candidateProperties = getApoProperties(candidate);
            return (
              candidate.key !== item.key &&
              String(candidateProperties._mws_cart || '') === cartId &&
              candidateProperties._mws_products &&
              !candidateProperties._mws_option
            );
          })
        : false;

      if (!hasLinkedParent) updates[item.key] = 0;
    });

    return updates;
  };

  const removeOrphanDeliveryLines = async (cart) => {
    const updates = getOrphanDeliveryUpdates(cart);
    if (Object.keys(updates).length === 0) return null;

    const response = await fetch(getCartRoute('cart_update_url', 'cart/update.js'), {
      ...getFetchConfig(),
      body: JSON.stringify({
        updates,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    });
    const state = await response.json();

    if (!response.ok || state.errors) {
      throw new Error(state.errors || `Delivery-line cleanup failed with status ${response.status}`);
    }

    return state;
  };

  const getApoProperties = (item) => item?.properties || {};

  const hasApoCartMetadata = (cart) =>
    (cart?.items || []).some((item) => {
      const properties = getApoProperties(item);
      return properties._mws_cart || properties._mws_products || properties._mws_option;
    });

  const parseApoProducts = (value) => {
    if (!value) return [];

    return String(value)
      .split(/;\s*/)
      .map((entry) => entry.split(','))
      .filter((parts) => parts.length >= 3)
      .map((parts) => ({
        optionId: parts[0],
        variantId: String(parts[1]),
        quantity: Number.parseInt(parts[2], 10),
      }))
      .filter((entry) => entry.optionId && entry.variantId && Number.isFinite(entry.quantity));
  };

  const isApoCartSynchronized = (cart) => {
    const apoItems = (cart?.items || []).filter((item) => getApoProperties(item)._mws_cart);
    if (apoItems.length === 0) return true;

    const groups = new Map();

    apoItems.forEach((item) => {
      const cartId = String(getApoProperties(item)._mws_cart);
      if (!groups.has(cartId)) groups.set(cartId, []);
      groups.get(cartId).push(item);
    });

    return Array.from(groups.values()).every((items) => {
      const parents = items.filter((item) => {
        const properties = getApoProperties(item);
        return properties._mws_products && !properties._mws_option;
      });
      const children = items.filter((item) => getApoProperties(item)._mws_option);

      if (parents.length === 0) return children.length === 0;

      const expectedChildren = new Map();

      for (const parent of parents) {
        const properties = getApoProperties(parent);
        const recordedQuantity = Number.parseInt(String(properties._mws_qty || ''), 10);

        if (Number.isFinite(recordedQuantity) && recordedQuantity !== parent.quantity) return false;

        parseApoProducts(properties._mws_products).forEach(({ optionId, variantId, quantity }) => {
          const key = `${optionId}:${variantId}`;
          expectedChildren.set(key, (expectedChildren.get(key) || 0) + quantity);
        });
      }

      for (const [key, expectedQuantity] of expectedChildren) {
        const actualQuantity = children.reduce((total, child) => {
          const properties = getApoProperties(child);
          const childKey = `${properties._mws_option}:${child.variant_id || child.id}`;
          return childKey === key ? total + child.quantity : total;
        }, 0);

        if (actualQuantity !== expectedQuantity) return false;
      }

      return children.every((child) => {
        const properties = getApoProperties(child);
        const key = `${properties._mws_option}:${child.variant_id || child.id}`;
        return expectedChildren.has(key);
      });
    });
  };

  const buildDeliverySyncPlan = (cart, line, quantity) => {
    const items = cart?.items || [];
    const parent = items[Number.parseInt(line, 10) - 1];
    const properties = getApoProperties(parent);
    const cartId = properties._mws_cart && String(properties._mws_cart);
    const previousQuantity = parent?.quantity;

    if (
      !parent ||
      !cartId ||
      !properties._mws_products ||
      properties._mws_option ||
      !Number.isInteger(previousQuantity) ||
      previousQuantity < 1 ||
      !/^\d+$/.test(String(properties._mws_qty || '')) ||
      Number.parseInt(properties._mws_qty, 10) !== previousQuantity
    ) {
      return null;
    }

    const parents = items.filter((item) => {
      const itemProperties = getApoProperties(item);
      return String(itemProperties._mws_cart || '') === cartId && itemProperties._mws_products && !itemProperties._mws_option;
    });

    if (parents.length !== 1 || parents[0].key !== parent.key) return null;

    const productEntries = String(properties._mws_products).split(/;\s*/);
    const childUpdates = {};
    const updatedProductEntries = [];

    for (const productEntry of productEntries) {
      const parts = productEntry.split(',');
      const optionId = parts[0];
      const variantId = String(parts[1] || '');
      const recordedQuantity = Number.parseInt(parts[2], 10);
      const matchingChildren = items.filter((item) => {
        const itemProperties = getApoProperties(item);
        return (
          String(itemProperties._mws_cart || '') === cartId &&
          String(itemProperties._mws_option || '') === optionId &&
          String(item.variant_id || item.id) === variantId
        );
      });

      if (
        parts.length < 3 ||
        !optionId ||
        !variantId ||
        !Number.isInteger(recordedQuantity) ||
        matchingChildren.length !== 1 ||
        matchingChildren[0].handle !== deliveryProductHandle ||
        matchingChildren[0].quantity !== recordedQuantity ||
        recordedQuantity % previousQuantity !== 0
      ) {
        return null;
      }

      const nextChildQuantity = (recordedQuantity / previousQuantity) * quantity;
      childUpdates[matchingChildren[0].key] = nextChildQuantity;
      parts[2] = String(nextChildQuantity);
      updatedProductEntries.push(parts.join(','));
    }

    if (updatedProductEntries.length === 0) return null;

    return {
      childUpdates,
      parentProperties: {
        ...properties,
        _mws_products: updatedProductEntries.join('; '),
        _mws_qty: String(quantity),
      },
    };
  };

  const fetchCart = async () => {
    const cartUrl = new URL(getCartRoute('cart_url', 'cart'), window.location.origin);
    cartUrl.pathname = `${cartUrl.pathname.replace(/\/$/, '')}.js`;
    cartUrl.searchParams.set('_cart_popup_timestamp', Date.now().toString());

    const response = await fetch(cartUrl.toString(), {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });

    if (!response.ok) throw new Error(`Cart refresh failed with status ${response.status}`);
    return response.json();
  };

  const waitForCartToSettle = async (initialCart) => {
    if (!hasApoCartMetadata(initialCart) || isApoCartSynchronized(initialCart)) return initialCart;

    const startedAt = Date.now();
    let latestCart = initialCart;

    // APO debounces cart mutations before reconciling its linked option products.
    // Waiting once here avoids repeatedly polling during that known idle period.
    await delay(apoMutationStartDelay);

    while (Date.now() - startedAt < maximumApoSettleTime) {
      try {
        latestCart = await fetchCart();
      } catch (error) {
        console.warn('[Cart popup] Could not verify the custom option products.', error);
        break;
      }

      if (isApoCartSynchronized(latestCart)) break;
      await delay(apoMutationInterval);
    }

    return latestCart;
  };

  const fetchDrawerSections = async () => {
    const sectionUrl = new URL(getCartRoute('cart_url', 'cart'), window.location.origin);
    sectionUrl.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
    sectionUrl.searchParams.set('_cart_popup_timestamp', Date.now().toString());

    const response = await fetch(sectionUrl.toString(), {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });

    if (!response.ok) throw new Error(`Cart sections failed with status ${response.status}`);
    return response.json();
  };

  const rememberRowSizes = (drawerItems) => {
    getItemRows(drawerItems).forEach((row) => {
      row.style.setProperty('--cart-popup-row-height', `${row.getBoundingClientRect().height}px`);
    });

    drawerItems.dataset.cartPopupPreviousMinHeight = drawerItems.style.minHeight;
    drawerItems.style.minHeight = `${drawerItems.getBoundingClientRect().height}px`;
  };

  const lockQuantityControls = (drawerItems) => {
    drawerItems.querySelectorAll('.quantity__button').forEach((button) => {
      button.dataset.cartPopupWasDisabled = button.disabled ? 'true' : 'false';
      button.disabled = true;
    });

    drawerItems.querySelectorAll('.quantity__input').forEach((input) => {
      input.dataset.cartPopupWasReadOnly = input.readOnly ? 'true' : 'false';
      input.readOnly = true;
    });
  };

  const unlockQuantityControls = (drawerItems) => {
    drawerItems.querySelectorAll('.quantity__button[data-cart-popup-was-disabled]').forEach((button) => {
      button.disabled = button.dataset.cartPopupWasDisabled === 'true';
      delete button.dataset.cartPopupWasDisabled;
    });

    drawerItems.querySelectorAll('.quantity__input[data-cart-popup-was-read-only]').forEach((input) => {
      input.readOnly = input.dataset.cartPopupWasReadOnly === 'true';
      delete input.dataset.cartPopupWasReadOnly;
    });
  };

  const beginStableUpdate = (drawerItems, line) => {
    const drawer = drawerItems?.closest('cart-drawer');
    if (!drawer || !drawerItems) return null;

    rememberRowSizes(drawerItems);
    lockQuantityControls(drawerItems);
    const activeRow = line ? drawerItems.querySelector(`[data-cart-line-index="${line}"]`) : null;
    activeRow?.classList.add(updatingRowClass);
    activeRow?.querySelector('.cart-popup__quantity-loader')?.removeAttribute('aria-hidden');
    drawer.classList.add(updatingClass);
    drawer.setAttribute('aria-busy', 'true');
    drawerItems.dataset.cartPopupUpdating = 'true';
    return drawer;
  };

  const finishStableUpdate = (drawerItems, drawer) => {
    const currentDrawerItems = drawer?.querySelector('cart-drawer-items');

    if (currentDrawerItems) {
      normalizeDeliveryProperty(currentDrawerItems);
      unlockQuantityControls(currentDrawerItems);
      currentDrawerItems.style.minHeight = currentDrawerItems.dataset.cartPopupPreviousMinHeight || '';
      delete currentDrawerItems.dataset.cartPopupPreviousMinHeight;
      delete currentDrawerItems.dataset.cartPopupUpdating;
      getItemRows(currentDrawerItems).forEach((row) => {
        row.style.removeProperty('--cart-popup-row-height');
        row.classList.remove(updatingRowClass);
        row.querySelector('.cart-popup__quantity-loader')?.setAttribute('aria-hidden', 'true');
      });
    }

    drawer?.classList.remove(updatingClass);
    if (!drawer?.classList.contains(pendingClass)) drawer?.removeAttribute('aria-busy');

    if (drawerItems && drawerItems !== currentDrawerItems) {
      delete drawerItems.dataset.cartPopupUpdating;
    }
  };

  const applyFinalDrawerState = async (
    drawerItems,
    fallbackSectionHtml,
    initialCart,
    fallbackCartBubbleHtml,
    { waitForApoMutation = false } = {},
  ) => {
    let cart = initialCart;
    let sectionHtml = fallbackSectionHtml;
    let cartBubbleHtml = fallbackCartBubbleHtml;

    if (!cart) {
      try {
        cart = await fetchCart();
      } catch (error) {
        console.warn('[Cart popup] Could not inspect the cart before refreshing the drawer.', error);
      }
    }

    if (waitForApoMutation) {
      await delay(apoMutationStartDelay);

      try {
        cart = await fetchCart();
      } catch (error) {
        console.warn('[Cart popup] Could not verify the cart after the linked product removal.', error);
      }
    }

    try {
      const cleanupState = await removeOrphanDeliveryLines(cart);

      if (cleanupState) {
        cart = cleanupState;
        sectionHtml = cleanupState.sections?.['cart-drawer'] || sectionHtml;
        cartBubbleHtml = cleanupState.sections?.['cart-icon-bubble'] || cartBubbleHtml;
      }
    } catch (error) {
      console.warn('[Cart popup] Could not remove an orphaned delivery line.', error);
    }

    const needsApoReconciliation = hasApoCartMetadata(cart) && !isApoCartSynchronized(cart);

    if (!needsApoReconciliation && sectionHtml) {
      reconcileDrawer(sectionHtml, { allowStructuralChanges: true });
      syncCartBubble(cartBubbleHtml);
      return drawerItems;
    }

    cart = await waitForCartToSettle(cart);

    try {
      const cleanupState = await removeOrphanDeliveryLines(cart);

      if (cleanupState) {
        reconcileDrawer(cleanupState.sections?.['cart-drawer'], { allowStructuralChanges: true });
        syncCartBubble(cleanupState.sections?.['cart-icon-bubble']);
        return drawerItems;
      }
    } catch (error) {
      console.warn('[Cart popup] Could not remove the settled orphaned delivery line.', error);
    }

    try {
      const sections = await fetchDrawerSections();
      reconcileDrawer(sections['cart-drawer'], { allowStructuralChanges: true });
      syncCartBubble(sections['cart-icon-bubble']);
    } catch (error) {
      console.error('[Cart popup] Could not load the final cart state.', error);
      reconcileDrawer(fallbackSectionHtml, { allowStructuralChanges: true });
    }

    return drawerItems;
  };

  const scheduleStableRefresh = (drawerItems) => {
    if (!drawerItems || !drawerItems.isConnected) return;

    if (drawerItems.dataset.cartPopupUpdating === 'true') {
      drawerItems.dataset.cartPopupRefreshQueued = 'true';
      return;
    }

    window.clearTimeout(drawerItems._cartPopupRefreshTimer);
    drawerItems._cartPopupRefreshTimer = window.setTimeout(async () => {
      if (!drawerItems.isConnected || drawerItems.dataset.cartPopupUpdating === 'true') return;

      const drawer = beginStableUpdate(drawerItems);
      if (!drawer) return;

      try {
        await applyFinalDrawerState(drawerItems);
      } finally {
        finishStableUpdate(drawerItems, drawer);
        clearPendingState(drawer);
      }
    }, 120);
  };

  const refreshCartAfterHistoryNavigation = async () => {
    const drawer = document.querySelector('cart-drawer');
    const drawerItems = drawer?.querySelector('cart-drawer-items');

    if (!drawer || !drawerItems || drawerItems.dataset.cartPopupUpdating === 'true') return;

    const stableDrawer = beginStableUpdate(drawerItems);

    try {
      const sections = await fetchDrawerSections();
      reconcileDrawer(sections['cart-drawer'], { allowStructuralChanges: true });
      syncCartBubble(sections['cart-icon-bubble']);
    } catch (error) {
      console.warn('[Cart popup] Could not restore the cart after returning from checkout.', error);
    } finally {
      finishStableUpdate(drawerItems, stableDrawer);
      clearPendingState(drawer);
    }
  };

  const updateDrawerQuantity = async (drawerItems, line, quantity, name, variantId) => {
    if (drawerItems.dataset.cartPopupUpdating === 'true') return;

    const drawer = beginStableUpdate(drawerItems, line);
    const quantityInput = drawerItems.querySelector(`#Drawer-quantity-${line}`);
    const previousQuantity = quantityInput?.getAttribute('value');
    let immediateSectionHtml;
    let deliverySyncPlan;

    if (!drawer) return;

    try {
      if (drawerItems.querySelector('[data-cart-popup-delivery-line]')) {
        try {
          deliverySyncPlan = buildDeliverySyncPlan(await fetchCart(), line, quantity);
        } catch (error) {
          console.warn('[Cart popup] Could not prepare the delivery-line fast path.', error);
        }
      }

      const requestPayload = {
        line,
        quantity,
        sections: drawerItems.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      };

      if (deliverySyncPlan) requestPayload.properties = deliverySyncPlan.parentProperties;

      const response = await fetch(getCartRoute('cart_change_url', 'cart/change.js'), {
        ...getFetchConfig(),
        body: JSON.stringify(requestPayload),
      });
      let parsedState = await response.json();

      if (!response.ok || parsedState.errors) {
        if (quantityInput && previousQuantity !== null) quantityInput.value = previousQuantity;
        drawerItems.updateLiveRegions(line, parsedState.errors || window.cartStrings.error);
        return;
      }

      if (deliverySyncPlan) {
        try {
          const deliveryResponse = await fetch(getCartRoute('cart_update_url', 'cart/update.js'), {
            ...getFetchConfig(),
            body: JSON.stringify({
              updates: deliverySyncPlan.childUpdates,
              sections: drawerItems.getSectionsToRender().map((section) => section.section),
              sections_url: window.location.pathname,
            }),
          });
          const deliveryState = await deliveryResponse.json();

          if (deliveryResponse.ok && !deliveryState.errors) {
            parsedState = deliveryState;
          } else {
            console.warn('[Cart popup] Delivery-line fast path was rejected; APO will finish the update.', deliveryState);
          }
        } catch (error) {
          console.warn('[Cart popup] Delivery-line fast path failed; APO will finish the update.', error);
        }
      }

      immediateSectionHtml = parsedState.sections?.['cart-drawer'];
      reconcileDrawer(immediateSectionHtml, { allowStructuralChanges: quantity === 0 });

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: parsedState,
          variantId,
        });
      }

      await applyFinalDrawerState(
        drawerItems,
        immediateSectionHtml,
        parsedState,
        parsedState.sections?.['cart-icon-bubble'],
        { waitForApoMutation: quantity === 0 && Boolean(deliverySyncPlan) },
      );

      const currentDrawerItems = drawer.querySelector('cart-drawer-items');
      currentDrawerItems?.updateLiveRegions(line, '');

      const activeRow = drawer.querySelector(`[data-cart-line-index="${line}"]`);
      const focusTarget = name ? activeRow?.querySelector(`[name="${name}"]`) : null;
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    } catch (error) {
      console.error('[Cart popup] Quantity update failed.', error);
      if (quantityInput && previousQuantity !== null) quantityInput.value = previousQuantity;

      const errors = document.getElementById('CartDrawer-CartErrors');
      if (errors) errors.textContent = window.cartStrings.error;
    } finally {
      finishStableUpdate(drawerItems, drawer);
    }
  };

  if (location.pathname.includes('/products/') && !document.querySelector('.ics-expand-btn-alt')) {
    const specsSentinel = document.createElement('button');
    specsSentinel.type = 'button';
    specsSentinel.hidden = true;
    specsSentinel.tabIndex = -1;
    specsSentinel.className = 'ics-expand-btn-alt';
    specsSentinel.setAttribute('aria-hidden', 'true');
    specsSentinel.setAttribute('data-cart-popup-specs-sentinel', '');
    document.body.append(specsSentinel);
  }

  const clearPendingState = (drawer) => {
    if (!drawer) return;

    window.clearTimeout(pendingTimeout);
    drawer.classList.remove(pendingClass);
    if (!drawer.classList.contains(updatingClass)) drawer.removeAttribute('aria-busy');
  };

  const showPendingState = (trigger) => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return;

    window.clearTimeout(pendingTimeout);
    drawer.classList.add(pendingClass);
    drawer.setAttribute('aria-busy', 'true');
    drawer.open(trigger);

    pendingTimeout = window.setTimeout(() => clearPendingState(drawer), 20000);
  };

  const isRequiredOptionComplete = (option) => {
    const controls = Array.from(option.querySelectorAll('.mw-option__control')).filter(
      (control) => !control.disabled,
    );

    if (controls.length === 0) return true;

    return controls.some((control) => {
      if (control.matches('[type="radio"], [type="checkbox"]')) return control.checked;
      return String(control.value || '').trim() !== '';
    });
  };

  const getIncompleteRequiredOptions = () =>
    Array.from(document.querySelectorAll('.mw-option--required')).filter(
      (option) => option.getClientRects().length > 0 && !isRequiredOptionComplete(option),
    );

  const showRequiredOptionErrors = (options) => {
    options.forEach((option) => {
      option.querySelector('.mw-option__helptext')?.classList.remove('mw-hidden');
      option.querySelectorAll('.mw-option__control').forEach((control) => {
        control.setAttribute('aria-invalid', 'true');
      });
    });

    const firstOption = options[0];
    firstOption?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    firstOption?.querySelector('.mw-option__control')?.focus({ preventScroll: true });
  };

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest(addToCartButtonSelector);
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

      const incompleteRequiredOptions = getIncompleteRequiredOptions();

      if (incompleteRequiredOptions.length > 0) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showRequiredOptionErrors(incompleteRequiredOptions);
        return;
      }

      showPendingState(button);
    },
    true,
  );

  document.addEventListener('change', (event) => {
    const option = event.target.closest('.mw-option--required');
    if (!option || !isRequiredOptionComplete(option)) return;

    option.querySelector('.mw-option__helptext')?.classList.add('mw-hidden');
    option.querySelectorAll('.mw-option__control').forEach((control) => {
      control.setAttribute('aria-invalid', 'false');
    });
  });

  window.addEventListener('pageshow', (event) => {
    const navigation = performance.getEntriesByType('navigation')[0];
    const returnedThroughHistory = event.persisted || navigation?.type === 'back_forward';

    if (!returnedThroughHistory) return;
    window.setTimeout(refreshCartAfterHistoryNavigation, 0);
  });

  customElements.whenDefined('cart-drawer-items').then(() => {
    const CartDrawerItemsElement = customElements.get('cart-drawer-items');
    const prototype = CartDrawerItemsElement?.prototype;

    if (!prototype || prototype.updateQuantity?.cartPopupStableUpdate) return;

    const stableUpdateQuantity = function (line, quantity, name, variantId) {
      return updateDrawerQuantity(this, line, quantity, name, variantId);
    };
    stableUpdateQuantity.cartPopupStableUpdate = true;
    prototype.updateQuantity = stableUpdateQuantity;

    const originalValidateQuantity = prototype.validateQuantity;
    const validateQuantityWithRemoval = function (event) {
      const quantity = Number.parseInt(event.target.value, 10);

      if (quantity === 0) {
        event.target.setCustomValidity('');
        event.target.reportValidity();
        this.updateQuantity(
          event.target.dataset.index,
          0,
          document.activeElement?.getAttribute('name'),
          event.target.dataset.quantityVariantId,
        );
        return;
      }

      return originalValidateQuantity.call(this, event);
    };
    validateQuantityWithRemoval.cartPopupStableUpdate = true;
    prototype.validateQuantity = validateQuantityWithRemoval;

    const stableCartUpdate = function () {
      scheduleStableRefresh(this);
    };
    stableCartUpdate.cartPopupStableUpdate = true;
    prototype.onCartUpdate = stableCartUpdate;
  });

  customElements.whenDefined('cart-drawer').then(() => {
    const CartDrawerElement = customElements.get('cart-drawer');
    const originalRenderContents = CartDrawerElement?.prototype?.renderContents;
    const originalOpen = CartDrawerElement?.prototype?.open;

    if (!originalRenderContents || originalRenderContents.cartPopupPendingBridge) return;

    if (originalOpen && !originalOpen.cartPopupStableOpen) {
      const openWithStableState = function (triggeredBy) {
        const drawerItems = this.querySelector('cart-drawer-items');
        const hasRows = getItemRows(drawerItems).length > 0;
        const isPendingAddition = this.classList.contains(pendingClass);

        if (this.classList.contains('is-empty') === hasRows) {
          this.classList.add(pendingClass);
          this.setAttribute('aria-busy', 'true');
        }

        const result = originalOpen.call(this, triggeredBy);
        if (!isPendingAddition) window.setTimeout(() => scheduleStableRefresh(drawerItems), 0);
        return result;
      };

      openWithStableState.cartPopupStableOpen = true;
      CartDrawerElement.prototype.open = openWithStableState;
    }

    const renderContentsWithPendingState = function (parsedState) {
      const result = originalRenderContents.call(this, parsedState);

      window.setTimeout(() => scheduleStableRefresh(this.querySelector('cart-drawer-items')), 0);
      return result;
    };

    renderContentsWithPendingState.cartPopupPendingBridge = true;
    CartDrawerElement.prototype.renderContents = renderContentsWithPendingState;
  });
})();
